import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, MessageCircle, Loader2, CheckCircle2, AlertCircle, ArrowRight, ArrowUpRight, Clock, Sparkles } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting"); setErrorMsg("");
    try {
      const payload = { ...form };
      if (!payload.phone) delete payload.phone;
      if (!payload.subject) delete payload.subject;
      await axios.post(`${API}/contact`, payload);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.response?.data?.detail || t.contact.error);
    }
  };

  return (
    <>
      <SEO title={t.seo.contact.title} description={t.seo.contact.description} keywords={t.seo.contact.keywords} path="/contact" />

      {/* ============ EDITORIAL HERO ============ */}
      <section className="relative pt-32 md:pt-36 pb-14 md:pb-20 bg-cream overflow-hidden">
        {/* Decorative dot grid + yellow blob */}
        <div className="absolute top-24 right-4 md:right-16 w-56 h-56 rounded-full bg-[#FFD500] blur-[110px] opacity-30 pointer-events-none" />
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(#0a0a0a 1px, transparent 1px)",
            backgroundSize: "22px 22px",
          }}
        />

        <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            {/* Left: editorial headline */}
            <div className="lg:col-span-8 anim-fade-up">
              <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur border border-zinc-200 px-3 py-1.5 rounded-full text-[11px] font-bold text-zinc-700 mb-6">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                </span>
                {t.contact.openLabel} · 24/7
              </div>

              <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900 leading-[1.1] max-w-3xl">
                <span className="font-serif italic font-normal text-zinc-500">{t.contact.badge}.</span>{" "}
                {t.contact.title.replace("Na Kontaktoni", "Le t'ju përgjigjemi").replace("Contact Us", "Let's hear from you").replace("Contattaci", "Facci sapere")}
              </h1>

              <p className="mt-5 text-zinc-600 text-base md:text-lg max-w-2xl leading-relaxed">
                {t.contact.subtitle}
              </p>
            </div>

            {/* Right: response stat card */}
            <div className="lg:col-span-4 anim-fade-up anim-delay-2">
              <div className="bg-zinc-900 text-white rounded-3xl p-6 md:p-7 shadow-taxi-lift relative overflow-hidden">
                <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-[#FFD500]/20 blur-xl" />
                <div className="relative">
                  <div className="flex items-center gap-2 text-[#FFD500] mb-3">
                    <Sparkles className="h-4 w-4" />
                    <span className="text-[10px] uppercase tracking-[0.2em] font-extrabold">{t.contact.responseLabel}</span>
                  </div>
                  <div className="font-display text-5xl md:text-6xl font-bold tracking-tight leading-none">
                    ~{t.contact.responseTime}
                  </div>
                  <div className="mt-4 flex items-center gap-2 text-zinc-400 text-sm">
                    <Clock className="h-4 w-4" />
                    <span>24 / 7 · Lushnjë</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ QUICK CONTACT STRIP (asymmetric channels) ============ */}
      <section className="bg-paper py-10 md:py-14 border-y border-zinc-100">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Phone - primary yellow */}
            <a
              href="tel:+355682222286"
              data-testid="contact-card-call"
              className="group relative overflow-hidden bg-[#FFD500] rounded-2xl p-6 hover:-translate-y-1 transition-all"
            >
              <div className="flex items-start justify-between mb-6">
                <Phone className="h-6 w-6 text-zinc-900" strokeWidth={2.2} />
                <ArrowUpRight className="h-5 w-5 text-zinc-900 opacity-40 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-zinc-800 mb-1.5">{t.contact.callTitle}</div>
              <div className="font-display text-lg md:text-xl font-bold text-zinc-900 leading-tight">+355 68 22 22 286</div>
            </a>

            {/* WhatsApp - green */}
            <a
              href="https://wa.me/355682222286"
              target="_blank" rel="noopener noreferrer"
              data-testid="contact-card-whatsapp"
              className="group relative overflow-hidden bg-[#25D366] rounded-2xl p-6 hover:-translate-y-1 transition-all"
            >
              <div className="flex items-start justify-between mb-6">
                <MessageCircle className="h-6 w-6 text-white" strokeWidth={2.2} />
                <ArrowUpRight className="h-5 w-5 text-white opacity-60 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-white/90 mb-1.5">{t.contact.whatsappTitle}</div>
              <div className="font-display text-lg md:text-xl font-bold text-white leading-tight">+355 68 22 22 286</div>
            </a>

            {/* Email - dark */}
            <a
              href="mailto:booking@brotherstaxi.al"
              data-testid="contact-card-email"
              className="group relative overflow-hidden bg-zinc-900 rounded-2xl p-6 hover:-translate-y-1 transition-all"
            >
              <div className="flex items-start justify-between mb-6">
                <Mail className="h-6 w-6 text-[#FFD500]" strokeWidth={2.2} />
                <ArrowUpRight className="h-5 w-5 text-[#FFD500] opacity-60 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-zinc-400 mb-1.5">{t.contact.emailTitle}</div>
              <div className="font-display text-base md:text-lg font-bold text-white leading-tight break-all">booking@brotherstaxi.al</div>
            </a>

            {/* Location - outline */}
            <a
              href="https://maps.google.com/?q=Lushnje%20Albania"
              target="_blank" rel="noopener noreferrer"
              data-testid="contact-card-location"
              className="group relative overflow-hidden bg-white border border-zinc-200 rounded-2xl p-6 hover:-translate-y-1 hover:border-zinc-900 transition-all"
            >
              <div className="flex items-start justify-between mb-6">
                <MapPin className="h-6 w-6 text-zinc-900" strokeWidth={2.2} />
                <ArrowUpRight className="h-5 w-5 text-zinc-900 opacity-40 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-zinc-500 mb-1.5">{t.contact.locationTitle}</div>
              <div className="font-display text-lg md:text-xl font-bold text-zinc-900 leading-tight">{t.contact.locationValue}</div>
            </a>
          </div>
        </div>
      </section>

      {/* ============ FORM + MAP ============ */}
      <section className="bg-mist py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-zinc-900 tracking-tight mb-3 leading-[1.05]">
              {t.contact.formTitle}
            </h2>
            <p className="text-zinc-600 mb-8 max-w-lg">{t.contact.formSubtitle}</p>

            {status === "success" ? (
              <div data-testid="contact-success" className="bg-white border-2 border-[#FFD500] rounded-3xl p-8 text-center shadow-taxi-yellow">
                <div className="h-14 w-14 bg-[#FFD500] rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-7 w-7 text-zinc-900" strokeWidth={2.5} />
                </div>
                <p className="text-zinc-900 text-lg font-bold mb-2">{t.common.success}</p>
                <p className="text-zinc-600">{t.contact.success}</p>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={onSubmit} className="bg-white border border-zinc-200 rounded-3xl p-8 shadow-taxi-card space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="bt-label">{t.contact.fields.name} *</label>
                    <input data-testid="contact-name-input" required name="name" value={form.name} onChange={onChange} className="bt-input" />
                  </div>
                  <div>
                    <label className="bt-label">{t.contact.fields.email} *</label>
                    <input data-testid="contact-email-input" required type="email" name="email" value={form.email} onChange={onChange} className="bt-input" />
                  </div>
                  <div>
                    <label className="bt-label">{t.contact.fields.phone}</label>
                    <input data-testid="contact-phone-input" type="tel" name="phone" value={form.phone} onChange={onChange} className="bt-input" />
                  </div>
                  <div>
                    <label className="bt-label">{t.contact.fields.subject}</label>
                    <input data-testid="contact-subject-input" name="subject" value={form.subject} onChange={onChange} className="bt-input" />
                  </div>
                </div>
                <div>
                  <label className="bt-label">{t.contact.fields.message} *</label>
                  <textarea data-testid="contact-message-input" required rows={5} name="message" value={form.message} onChange={onChange} className="bt-input resize-none" />
                </div>
                {status === "error" && (
                  <div data-testid="contact-error" className="flex items-center gap-2 text-red-600 text-sm font-semibold">
                    <AlertCircle className="h-4 w-4" /> {errorMsg}
                  </div>
                )}
                <button data-testid="contact-submit-button" type="submit" disabled={status === "submitting"} className="bt-btn-primary">
                  {status === "submitting" ? (<><Loader2 className="h-4 w-4 animate-spin" /> {t.common.submitting}</>) : (<>{t.contact.submit}<ArrowRight className="h-4 w-4" /></>)}
                </button>
              </form>
            )}
          </div>

          <div className="space-y-6">
            {/* WhatsApp quick chat card */}
            <a
              href="https://wa.me/355682222286"
              target="_blank" rel="noopener noreferrer"
              data-testid="contact-quick-whatsapp"
              className="block bg-white border border-zinc-200 rounded-3xl p-6 md:p-7 shadow-taxi-card hover:shadow-taxi-lift hover:-translate-y-0.5 transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 bg-[#25D366] rounded-2xl flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="h-6 w-6 text-white" strokeWidth={2.2} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-display text-lg md:text-xl font-bold text-zinc-900 tracking-tight">{t.contact.quickTitle}</h3>
                    <ArrowUpRight className="h-5 w-5 text-zinc-400 group-hover:text-zinc-900 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                  </div>
                  <p className="text-zinc-600 text-sm mt-1.5 leading-relaxed">{t.contact.quickDesc}</p>
                </div>
              </div>
            </a>

            {/* Map */}
            <div className="bg-white border border-zinc-200 rounded-3xl overflow-hidden shadow-taxi-card">
              <iframe
                data-testid="google-maps-iframe"
                title="Brothers Taxi - Lushnje Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3035.7681!2d19.7050!3d40.9419!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sLushnje%2C%20Albania!5e0!3m2!1sen!2sus!4v1700000000000"
                width="100%"
                height="420"
                style={{ border: 0, display: "block" }}
                allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
